import { useState } from 'react'
import { Link, router } from '@inertiajs/react'
import { Pencil, Trash2 } from 'lucide-react'
import { BackofficeLayout } from '@/components/layout/BackofficeLayout'
import { DataTable } from '@/components/backoffice/DataTable'
import { SectionHeader } from '@/components/backoffice/SectionHeader'
import { Button } from '@/components/ui/button'
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { CountryWithStates } from '@/types'

type Props = { country: CountryWithStates }

export default function CountriesShow({ country }: Props) {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [deleteStateId, setDeleteStateId] = useState<string | null>(null)

  return (
    <BackofficeLayout title={country.name} breadcrumbs={[{ label: 'Backoffice', href: '/backoffice/dashboard' }, { label: 'Jurisdictions', href: '/backoffice/countries' }, { label: country.name }]}>
      <div className="space-y-6">
        <div className="mb-8">
          <h1 className="text-xl font-semibold tracking-tight">{country.name}</h1>
          <p className="text-sm text-muted-foreground">{country.code}</p>
        </div>

        <div className="flex gap-2">
          <Button size="sm" variant="outline" asChild className="h-8 text-[13px]">
            <Link href={`/backoffice/countries/${country.id}/edit`}><Pencil className="mr-1.5 h-3 w-3" />Edit</Link>
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setShowDeleteDialog(true)} className="h-8 text-[13px] text-destructive hover:text-destructive">
            <Trash2 className="mr-1.5 h-3 w-3" />Delete
          </Button>
        </div>

        <div>
          <SectionHeader title="States / Provinces" count={country.states.length} action={{ label: 'Add State', href: `/backoffice/countries/${country.id}/states/new` }} />
          <DataTable
            data={country.states}
            keyFn={s => s.id}
            onRowClick={s => router.visit(`/backoffice/countries/${country.id}/states/${s.id}`)}
            emptyMessage="No states added yet."
            columns={[
              { key: 'name', header: 'Name', className: 'flex-1', render: s => (
                <Link href={`/backoffice/countries/${country.id}/states/${s.id}`} className="text-sm font-medium hover:underline">{s.name}</Link>
              )},
              { key: 'code', header: 'Code', className: 'w-24', render: s => (
                <span className="text-sm font-mono text-muted-foreground">{s.code}</span>
              )},
              { key: 'areas', header: 'Local Areas', className: 'w-28', render: s => (
                <span className="text-sm text-muted-foreground">{s.local_areas_count}</span>
              )},
              { key: 'entities', header: 'Entities', className: 'w-20', render: s => (
                <span className="text-sm text-muted-foreground">{s.entities_count}</span>
              )},
              { key: 'actions', header: '', className: 'w-20', render: s => (
                <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button size="icon" variant="ghost" asChild className="h-6 w-6">
                    <Link href={`/backoffice/countries/${country.id}/states/${s.id}/edit`}><Pencil className="h-3 w-3" /></Link>
                  </Button>
                  <Button size="icon" variant="ghost" onClick={(e) => { e.stopPropagation(); setDeleteStateId(s.id) }} className="h-6 w-6">
                    <Trash2 className="h-3 w-3 text-destructive" />
                  </Button>
                </div>
              )},
            ]}
          />
        </div>

        <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete {country.name}?</AlertDialogTitle>
              <AlertDialogDescription>Countries with associated entities cannot be deleted.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={() => router.delete(`/backoffice/countries/${country.id}`)}>Delete</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <AlertDialog open={!!deleteStateId} onOpenChange={() => setDeleteStateId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete this state?</AlertDialogTitle>
              <AlertDialogDescription>States with associated entities cannot be deleted.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={() => { if (deleteStateId) { router.delete(`/backoffice/countries/${country.id}/states/${deleteStateId}`); setDeleteStateId(null) } }}>Delete</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </BackofficeLayout>
  )
}
